import StatusTooltip from "components/StatusTooltip";
import React, { createContext, useContext, useState } from "react";

interface TooltipContextProps {
  showTooltip: (
    message: string,
    type?: "success" | "error" | "info",
    header?: string
  ) => void;
}

const TooltipContext = createContext<TooltipContextProps | undefined>(
  undefined
);

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tooltip, setTooltip] = useState<{
    header?: string;
    message: string;
    type: "success" | "error" | "info";
    visible: boolean;
  }>({
    header: "",
    message: "",
    type: "info",
    visible: false,
  });

  const showTooltip = (
    message: string,
    type: "success" | "error" | "info" = "info",
    header?: string
  ) => {
    setTooltip({ header, message, type, visible: true });

    const timer = setTimeout(() => {
      setTooltip((prev) => ({ ...prev, visible: false }));
    }, 3000); // Auto-hide after 3 seconds

    return () => clearTimeout(timer); // Cleanup timer on unmount
  };

  return (
    <TooltipContext.Provider value={{ showTooltip }}>
      {children}
      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            top: 24,
            left: 24,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <StatusTooltip
            key={`${tooltip.message}-${tooltip.type}`}
            message={tooltip.message}
            header={tooltip?.header || undefined}
            type={tooltip.type}
            onClose={() => setTooltip((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextProps => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error("useTooltip must be used within a TooltipProvider");
  }
  return context;
};
